import React, { useEffect } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm, Controller } from 'react-hook-form';
import { useServerRequest } from 'src/hooks/useServerRequest';
import { useSnackbar } from 'src/components/snackbar';
import { useLocales } from 'src/locales';
import { updateIssuedCheck, updateReceivedCheckInRib } from 'src/redux/slices/dataSlice';
import { useDispatch } from 'react-redux';

interface CheckDialogProps {
  open: boolean;
  onClose: () => void;
  id: string;
  typo: number;
  rib: number;
  company: number;
  carnet: number | undefined | null
}

const DueDateDialog: React.FC<CheckDialogProps> = ({ open, onClose, id, typo, rib, company, carnet }) => {
  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      dueDate: null,
    },
  });

  const { makeRequest } = useServerRequest();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLocales();
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      reset({ dueDate: null });
    }
  }, [open, reset]);

  const onSubmit = async (datas: any) => {
    if (!datas.dueDate) {
      enqueueSnackbar(t('common.input.dueDate.error1'), { variant: 'error' });
      return;
    }
    const formattedDueDate = format(datas.dueDate, 'yyyy-MM-dd HH:mm:ss');
    const newId = parseInt(id, 10);
    const data = {
      check_id: id,
      due_date: formattedDueDate,
      type: typo,
    };

    const response = await makeRequest(`/updateDueDate`, {
      method: 'POST',
      data,
    }, t('common.input.dueDate.success'));

    if (response.success) {
      if (typo === 2) {
        dispatch(
          updateReceivedCheckInRib({
            companyId: company,
            ribId: rib,
            receivedCheck: { id: newId, due_date: formattedDueDate },
          })
        );
      } else if (carnet) {
        dispatch(
          updateIssuedCheck({
            companyId: company,
            ribId: rib,
            carnetId: carnet,
            check: { id: newId, due_date: formattedDueDate },
          })
        )
      }
      onClose()
    }
  };

  return (
    <Dialog maxWidth="sm" open={open} onClose={(_, reason) => reason !== 'backdropClick' && onClose()}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {t('m.t.dueDate')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: '10px' }}>
            <Grid item xs={12}>
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label={t('common.input.dueDate.label')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />

            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="inherit">
            {t('common.button.action.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {t('common.button.action.submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DueDateDialog;
